import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { Eye, EyeSlash, ArrowRight, Briefcase, ShieldCheck, Vault } from "@phosphor-icons/react";
import { useAuth } from "../contexts/AuthContext";
import { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";

export default function Login() {
    const { user, login } = useAuth();
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPwd, setShowPwd] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");

    // Post-login landing (Iter 45.6.2, 2026-07-14 · updated per operator
    // directive after the Anurag/Developer bug):
    //
    //   Predicate-aware landing that never sends a user to a page they
    //   can't see:
    //     • DATA_USER / LOGO_USER → /data/companies (backend chokepoint
    //       hard-locks them to the Company Repository).
    //     • Employee-linked user → /attendance/me (they hold access via
    //       the RequireLinkedEmployee gate).
    //     • Everyone else (admins, HR back-office users, unlinked ops
    //       users) → /dashboard.
    //
    //   Fixes the Iter 45.6.1 regression where users without ESS
    //   grants got redirected to /profile by the RequirePerm fallback.
    const landingFor = (u) => {
        if (u?.role === "data_user" || u?.role === "logo_user") return "/data/companies";
        if (u?.is_employee_linked) return "/attendance/me";
        return "/dashboard";
    };

    if (user) return <Navigate to={landingFor(user)} replace />;

    const submit = async (e) => {
        e?.preventDefault();
        setError("");
        setSubmitting(true);
        try {
            const { user: loggedIn } = await login(email.trim(), password);
            toast.success("Signed in");
            navigate(landingFor(loggedIn));
        } catch (err) {
            const msg = formatApiError(err, "Sign-in failed");
            setError(msg);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen grid lg:grid-cols-2 bg-slate-50" data-testid="login-page">
            {/* Left: form */}
            <div className="flex items-center justify-center px-6 sm:px-12 py-10">
                <div className="w-full max-w-sm">
                    <div className="flex items-center gap-2.5 mb-12">
                        <div className="h-8 w-8 bg-slate-900 rounded-md flex items-center justify-center">
                            <span className="text-white font-display font-bold text-sm">R</span>
                        </div>
                        <div>
                            <div className="font-display font-bold tracking-tight text-slate-900">REEDOS</div>
                            <div className="reedos-eyebrow text-[9px]">Billing &amp; Commercial</div>
                        </div>
                    </div>

                    <div className="reedos-eyebrow mb-3">Sign in</div>
                    <h1 className="font-display text-3xl sm:text-4xl tracking-tight font-bold text-slate-900 leading-[1.05]">
                        Commercial control,<br />without the chaos.
                    </h1>
                    <p className="text-sm text-slate-500 mt-3">
                        Manage Purchase Orders, Invoices and Payments — one source of truth for every commercial transaction at REEDOS.
                    </p>

                    <form onSubmit={submit} className="mt-8 space-y-4" data-testid="login-form">
                        <div>
                            <Label htmlFor="email" className="text-xs font-semibold text-slate-700">Email</Label>
                            <Input
                                id="email"
                                type="email"
                                autoComplete="email"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="you@reedos.in"
                                data-testid="login-email-input"
                                className="mt-1.5 h-10 bg-white"
                            />
                        </div>
                        <div>
                            <Label htmlFor="password" className="text-xs font-semibold text-slate-700">Password</Label>
                            <div className="relative mt-1.5">
                                <Input
                                    id="password"
                                    type={showPwd ? "text" : "password"}
                                    autoComplete="current-password"
                                    required
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    data-testid="login-password-input"
                                    className="h-10 bg-white pr-10"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPwd((s) => !s)}
                                    className="absolute inset-y-0 right-2 flex items-center text-slate-400 hover:text-slate-700"
                                    aria-label="Toggle password visibility"
                                    data-testid="login-toggle-password"
                                >
                                    {showPwd ? <EyeSlash size={18} /> : <Eye size={18} />}
                                </button>
                            </div>
                        </div>

                        {error && (
                            <div data-testid="login-error" className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">
                                {error}
                            </div>
                        )}

                        <Button
                            type="submit"
                            disabled={submitting}
                            data-testid="login-submit-btn"
                            className="w-full h-10 bg-slate-900 hover:bg-slate-800 text-white font-medium gap-1.5"
                        >
                            {submitting ? "Signing in..." : "Sign in"}
                            {!submitting && <ArrowRight size={16} weight="bold" />}
                        </Button>

                        <div className="text-center pt-2">
                            <a
                                href="/forgot-password"
                                className="text-[11px] font-semibold text-slate-500 hover:text-slate-900 underline-offset-2 hover:underline transition-colors"
                                data-testid="login-forgot-password-link"
                            >
                                Forgot your password?
                            </a>
                        </div>
                    </form>
                </div>
            </div>

            {/* Right: branded panel */}
            <div className="hidden lg:flex bg-slate-900 text-white p-12 relative overflow-hidden">
                <div className="absolute inset-0 opacity-[0.06]" style={{
                    backgroundImage:
                        "linear-gradient(to right, white 1px, transparent 1px), linear-gradient(to bottom, white 1px, transparent 1px)",
                    backgroundSize: "44px 44px",
                }} />
                <div className="relative z-10 flex flex-col justify-between w-full">
                    <div>
                        <div className="reedos-eyebrow text-slate-400">Platform</div>
                        <h2 className="font-display text-4xl tracking-tight font-bold mt-2 leading-[1.1]">
                            Single source<br />of truth for every<br />commercial transaction.
                        </h2>
                        <p className="text-slate-300 text-sm mt-4 max-w-md">
                            Purchase Orders with versioning. Invoices auto-generated from finalized POs. Partial payments. GST-ready. AMC pipeline. All wired to REEDOS.
                        </p>
                    </div>

                    {/* Pre-login pillars — outcome-oriented value proposition.
                        Replaced earlier KPI ledger preview to keep public surface
                        focused on platform purpose, not operational statistics. */}
                    <div className="grid grid-cols-3 gap-px bg-slate-800 rounded-lg overflow-hidden mt-10" data-testid="login-pillars">
                        {[
                            {
                                icon: Briefcase,
                                label: "Commercial Operations",
                                desc: "Manage proposals, purchase orders, invoices, payments, AMC renewals, and collections from a single platform.",
                                testid: "login-pillar-operations",
                            },
                            {
                                icon: ShieldCheck,
                                label: "Compliance & Controls",
                                desc: "GST, TDS, audit trails, approvals, corrections, and financial reporting built into the workflow.",
                                testid: "login-pillar-compliance",
                            },
                            {
                                icon: Vault,
                                label: "Single Source of Truth",
                                desc: "Maintain a complete commercial history with traceability across transactions, settlements, credit notes, and reconciliations.",
                                testid: "login-pillar-truth",
                            },
                        ].map((p) => (
                            <div key={p.label} className="bg-slate-900 px-5 py-5 flex flex-col gap-2.5" data-testid={p.testid}>
                                <p.icon size={20} weight="duotone" className="text-slate-300" />
                                <div className="font-display text-sm font-bold text-white tracking-tight leading-tight">{p.label}</div>
                                <div className="text-[11px] text-slate-400 leading-relaxed">{p.desc}</div>
                            </div>
                        ))}
                    </div>

                    <div className="text-[11px] text-slate-500 tracking-wide mt-10">
                        REEDOS · Billing &amp; Commercial Management System (BCMS)
                    </div>
                </div>
            </div>
        </div>
    );
}
